/**
 * PlaylistSearchForm Component
 *
 * Search form with date, time, and channel selection.
 * Displays in the accent-colored header section.
 */

import { Component } from "solid-js";
import { Heading } from "~/components/typography";
import Button from "~/components/ui/Button/Button";
import { MagnifyingGlassIcon } from "~/components/ui/icons";
import Select from "~/components/ui/Select/Select";
import { getTodayDate } from "../utils";
import styles from "./PlaylistSearchForm.module.css";

export interface Channel {
  identifier: string;
  displayName: string;
}

export interface PlaylistSearchFormProps {
  /** Available channels for the dropdown */
  channels: Channel[];
  /** Currently selected date (YYYY-MM-DD) */
  selectedDate: string;
  /** Currently selected time (HH:MM) */
  selectedTime: string;
  /** Currently selected channel identifier */
  selectedChannel: string;
  /** Callback when date changes */
  onDateChange: (date: string) => void;
  /** Callback when time changes */
  onTimeChange: (time: string) => void;
  /** Callback when channel changes */
  onChannelChange: (channelId: string) => void;
  /** Callback when form is submitted */
  onSubmit: (e: Event) => void;
}

const PlaylistSearchForm: Component<PlaylistSearchFormProps> = (props) => {
  return (
    <section class={styles.searchHeaderSection}>
      <div class={styles.searchHeaderContainer}>
        <div class={styles.headerContent}>
          <Heading variant="pageTitle" as="h1" class={styles.title}>
            Playlistsuche
          </Heading>
          <p class={styles.description}>
            Pop, Rock, Mega-Schlager, Super-Oldies, Musik mit Gefühl zum Entspannen, die Favorites
            aus den Charts und ganz neue Songs – hier findet Ihr jeden Titel, der bei Absolut Radio
            läuft.
          </p>
        </div>

        <form class={styles.searchForm} onSubmit={props.onSubmit}>
          <div class={styles.formRow}>
            <div class={styles.formGroup}>
              <label class={styles.label} for="date">
                Datum
              </label>
              <input
                type="date"
                id="date"
                class={styles.input}
                value={props.selectedDate}
                onInput={(e) => props.onDateChange(e.currentTarget.value)}
                max={getTodayDate()}
                placeholder="Datum auswählen"
              />
            </div>

            <div class={styles.formGroup}>
              <label class={styles.label} for="time">
                Uhrzeit
              </label>
              <input
                type="time"
                id="time"
                class={styles.input}
                value={props.selectedTime}
                onInput={(e) => props.onTimeChange(e.currentTarget.value)}
                placeholder="Uhrzeit auswählen"
              />
            </div>

            <div class={styles.formGroup}>
              <label class={styles.label} for="channel">
                Sender
              </label>
              <Select
                id="channel"
                name="channel"
                variant="onAccent"
                options={props.channels.map((ch) => ({
                  value: ch.identifier,
                  label: ch.displayName,
                }))}
                value={props.selectedChannel}
                placeholder="Sender auswählen"
                onInput={props.onChannelChange}
              />
            </div>

            <Button
              type="submit"
              variant="primaryFixed"
              icon={<MagnifyingGlassIcon />}
              class={styles.searchButton}
            >
              Suchen
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default PlaylistSearchForm;
